import { useScreenSize } from '@hooks';
import { utilApi, utilSlice } from '@store';
import { Flex, TextInput, theme, Typography } from '@styles';
import { ReactComponent as InputIcon } from '@svgs/reportInput.svg';
import { bool, func, number } from 'prop-types';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { Icon } from '../Icon';
import { Line } from '../Line';
import { Modal } from '../Modal';
import {
  choosingOptionsMocks,
  TextArea,
} from './ReportModalLayout.layoutProps';

const ReportModalLayout = ({
  activityId,
  isLessonActivity,
  isVisible,
  setIsVisible,
}) => {
  const { i18n } = useTranslation();
  const { isTabletSM } = useScreenSize();
  const dispatch = useDispatch();

  const [reportOption, setReportOption] = useState(null);
  const [reportText, setReportText] = useState('');

  const [report] = utilApi.useReportMutation();

  const handleClearState = () => {
    setReportText('');
    setReportOption(null);
  };

  const handleSendReport = async () => {
    const res = await report({
      content_type: isLessonActivity ? 'lesson_activity' : 'scenario',
      id: activityId,
      message: reportText,
      type: choosingOptionsMocks?.[reportOption]?.type,
    });

    if (!res.error) {
      dispatch(
        utilSlice.actions.setToastConfig({
          disappearTimeoutInSeconds: 3,
          text: i18n.t('ReportModal.Toast.Success'),
          variant: 'success',
        })
      );
    } else {
      dispatch(
        utilSlice.actions.setToastConfig({
          disappearTimeoutInSeconds: 3,
          text: i18n.t('ReportModal.Toast.Error'),
          variant: 'error',
        })
      );
    }

    handleClearState();
  };

  const handleChangeReportOption = (optionId) => () => {
    setReportOption(optionId);
  };

  const handleChangeReportText = (e) => {
    setReportText(e.target.value);
  };

  const choosingOptions = useMemo(() =>
    Object.keys(choosingOptionsMocks).map((optionId) => (
      <label htmlFor={`report-option-${optionId}`}>
        <Flex alignItems="center">
          <TextInput
            checked={optionId === reportOption}
            id={`report-option-${optionId}`}
            m={0}
            name="report"
            onChange={handleChangeReportOption(optionId)}
            p={0}
            type="radio"
            value={optionId}
          />
          <Typography
            color={theme.colors.white[100]}
            ml={12}
            variant="MS-regular-s16-lh25"
          >
            {i18n.t(choosingOptionsMocks[optionId].titleText)}
          </Typography>
        </Flex>
      </label>
    ))
  );

  return (
    <Modal
      firstButtonProps={{
        color: theme.colors.white[100],
        px: isTabletSM ? 47 : 132,
        py: 22,
        variant: 'secondary',
      }}
      firstButtonText={i18n
        .t('ProfilePage.DeleteProfile.Modal.Cancel')
        .toUpperCase()}
      isVisible={isVisible}
      modalContainerProps={{
        backgroundColor: theme.colors.white[10],
        border: `1px solid ${theme.colors.white[10]}`,
        borderRadius: '0px',
        height: '100%',
        maxHeight: '715px',
      }}
      modalMaxHeight="100%"
      onFirstButtonClick={handleClearState}
      onSecondButtonClick={handleSendReport}
      secondButtonProps={{
        disabled: reportOption === null || !reportText,
        px: isTabletSM ? 47 : 132,
        py: 22,
        variant: 'primary',
      }}
      secondButtonText={i18n.t('ReportModal.Send').toUpperCase()}
      setModalVisible={setIsVisible}
      withBackdropNoise
      withBottomButtons
    >
      <Flex flexDirection="column" p={isTabletSM ? 16 : 40}>
        <Typography
          color={theme.colors.white[100]}
          mb={2}
          variant="MS-semibold-s24-lh36"
        >
          {i18n.t('ReportModal.HelpImprove')}
        </Typography>
        <Typography
          color={theme.colors.alto[100]}
          mb={4}
          variant="MS-regular-s16-lh25"
        >
          {i18n.t('ReportModal.Feedback')}
        </Typography>
        <Typography
          color={theme.colors.white[100]}
          mb={12}
          variant="MS-semibold-s18-lh19"
        >
          {i18n.t('ReportModal.ChooseOption')}
        </Typography>
        <Flex flexDirection="column" gap={8}>
          {choosingOptions}
        </Flex>
        <Line my={4} />
        <Typography
          color={theme.colors.white[100]}
          mb={3}
          variant="MS-semibold-s18-lh19"
        >
          {i18n.t('ReportModal.InputYourMessage')}
        </Typography>
        <Flex>
          <Flex
            backgroundColor={theme.colors.white[10]}
            height={130}
            pl={24}
            pt={24}
          >
            <Icon SVG={InputIcon} />
          </Flex>
          <TextArea
            onChange={(e) => handleChangeReportText(e)}
            placeholder={i18n.t('ReportModal.InputMessage')}
            value={reportText}
          />
        </Flex>
      </Flex>
    </Modal>
  );
};

ReportModalLayout.propTypes = {
  activityId: number.isRequired,
  isLessonActivity: bool.isRequired,
  isVisible: bool.isRequired,
  setIsVisible: func.isRequired,
};

export default ReportModalLayout;
